import React, { useState, useRef, useEffect } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Check, ImageIcon, Loader2, UploadCloud, X } from 'lucide-react';
import { Alert, Button, DialogOverlay } from '../primitives';
import { cn } from '../../lib/utils';
import { useImages } from './ImagesProvider';
import type { Image } from './types';

export interface ImageSelectModalProps {
    open: boolean;
    onClose: () => void;
    /** 'multiple' picks a gallery; 'single' picks one variant image. */
    mode: 'multiple' | 'single';
    /** Selection to restore each time the modal opens. */
    selectedIds: string[];
    /**
     * Called with the final selection on Done/Save. In single mode the array
     * is empty (unset) or has exactly one id. In multiple mode previously
     * selected images come first, images uploaded this session last.
     */
    onDone: (_ids: string[]) => void;
    productId?: string | null;
}

/**
 * One picture in the library, and whether it is picked.
 *
 * The whole tile is the control. It used to be a checkbox floating in the
 * corner of a div that also had its own click handler, so the input and the
 * tile each toggled the same thing and had to stop each other's events to
 * avoid cancelling out. A button with `aria-checked` is one control, reachable
 * by keyboard, and still announces itself as a checkbox or a radio.
 */
const ImageTile: React.FC<{
    image: Image;
    checked: boolean;
    single: boolean;
    onToggle: () => void;
}> = ({ image, checked, single, onToggle }) => (
    <button
        type="button"
        role={single ? 'radio' : 'checkbox'}
        aria-checked={checked}
        aria-label={image.loading ? 'Uploading image' : 'Library image'}
        disabled={image.loading}
        onClick={onToggle}
        data-testid="library-image"
        className={cn(
            'group relative aspect-square overflow-hidden rounded-lg border bg-muted transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            checked ? 'border-primary ring-1 ring-primary' : 'border-border hover:border-primary/50',
        )}
    >
        <img
            src={image.loading ? image.url : image.thumbnailUrl || image.url}
            alt=""
            className={cn('h-full w-full object-cover', image.loading && 'opacity-40')}
        />

        {/* The tick sits on the picture, so it needs its own ground to be
            legible over a photograph of anything. */}
        <span
            className={cn(
                'absolute left-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full border transition-colors',
                checked
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-white/70 bg-foreground/20 opacity-0 group-hover:opacity-100',
            )}
        >
            {checked && <Check className="h-3 w-3" aria-hidden />}
        </span>

        {image.loading && (
            <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-card/70 text-[11px] text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden />
                Uploading
            </span>
        )}
    </button>
);

/**
 * The media library: every picture this seller has uploaded, and the way to
 * add more.
 *
 * Rebuilt on the design system. It was the last overlay in the app running on
 * its own stylesheet — `bg-gray-700`, `text-blue-500`, a hardcoded green tick —
 * with title-case copy, no empty state, and an upload failure that opened a
 * second dialog on top of this one so the library disappeared behind the
 * message about it. Review: "this is a media library thing that is just
 * completely missing from our screens and designs."
 */
const ImageSelectModal: React.FC<ImageSelectModalProps> = ({
    open,
    onClose,
    mode,
    selectedIds,
    onDone,
    productId = null,
}) => {
    const single = mode === 'single';
    const [error, setError] = useState<string | null>(null);
    const [localSelectedIds, setLocalSelectedIds] = useState<string[]>([]);
    const [uploadedIds, setUploadedIds] = useState<string[]>([]);
    const [dragging, setDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const { images, uploadImage, fetchMoreImages, loading, loadingMore } = useImages();

    useEffect(() => {
        if (open) {
            setLocalSelectedIds([...selectedIds]);
            setUploadedIds([]);
            setError(null);
        }
        // selectedIds is intentionally only read when the modal opens.
    }, [open]);

    const handleFileSelect = (files: FileList) => {
        if (single && files.length > 1) {
            setError('Choose one image at a time for a variant.');
            return;
        }
        setError(null);
        Array.from(files).forEach((file) => {
            uploadImage(file, productId)
                .then((uploadedImage) => {
                    if (uploadedImage) {
                        setUploadedIds((prev) => (single ? [uploadedImage.id] : [...prev, uploadedImage.id]));
                        setLocalSelectedIds((prev) =>
                            single ? [uploadedImage.id] : [...prev, uploadedImage.id],
                        );
                    }
                })
                .catch(() => setError(`${file.name} could not be uploaded. Try again.`));
        });
    };

    const toggleImageSelection = (image: Image) => {
        setLocalSelectedIds((prev) => {
            if (prev.includes(image.id)) {
                return prev.filter((id) => id !== image.id);
            }
            return single ? [image.id] : [...prev, image.id];
        });
    };

    const handleDone = () => {
        if (single) {
            onDone(localSelectedIds);
        } else {
            // Previously selected images keep their position; this session's
            // uploads go last.
            const existing = localSelectedIds.filter((id) => !uploadedIds.includes(id));
            const uploaded = localSelectedIds.filter((id) => uploadedIds.includes(id));
            onDone([...existing, ...uploaded]);
        }
        onClose();
    };

    /** More of the library, once the grid is scrolled to its end. */
    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const el = event.currentTarget;
        if (el.scrollHeight - el.clientHeight - el.scrollTop < 40) {
            void fetchMoreImages(productId);
        }
    };

    const uploadingCount = images.filter((image) => image.loading).length;
    const chosen = localSelectedIds.length;

    const sortedImages = [
        ...images.filter((img) => uploadedIds.includes(img.id)),
        ...images.filter((img) => !uploadedIds.includes(img.id)),
    ];

    return (
        <DialogPrimitive.Root
            open={open}
            onOpenChange={(nextOpen) => {
                if (!nextOpen) onClose();
            }}
        >
            <DialogPrimitive.Portal>
                <DialogOverlay className="z-[100]" />
                <DialogPrimitive.Content
                    aria-describedby="media-library-purpose"
                    className="fixed left-1/2 top-1/2 z-[100] flex max-h-[86vh] w-[min(52rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl focus:outline-none"
                >
                    <div className="flex items-start gap-3 border-b border-border px-6 py-4">
                        <div className="min-w-0">
                            <DialogPrimitive.Title asChild>
                                <h2 className="text-base font-semibold text-foreground">
                                    {single ? 'Choose an image' : 'Add images'}
                                </h2>
                            </DialogPrimitive.Title>
                            <p id="media-library-purpose" className="mt-0.5 text-xs text-muted-foreground">
                                {/* What the grid below actually is. "Select Image" named
                                    the button, not the place, so a seller had no way to
                                    tell that pictures they uploaded once were still here. */}
                                Everything you have uploaded, and anything you add now. Pictures stay in
                                your library for other products.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="ml-auto rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        >
                            <X className="h-4 w-4" />
                            <span className="sr-only">Close</span>
                        </button>
                    </div>

                    <div
                        className="flex-1 overflow-y-auto px-6 py-5"
                        onScroll={handleScroll}
                        data-testid="library-body"
                    >
                        {error && (
                            <div className="mb-4">
                                {/* Inline, above the library. A failed upload used to open
                                    a second dialog over this one, so the thing you were
                                    doing vanished behind the news about it. */}
                                <Alert variant="destructive">{error}</Alert>
                            </div>
                        )}

                        <div
                            onDrop={(event) => {
                                event.preventDefault();
                                setDragging(false);
                                handleFileSelect(event.dataTransfer.files);
                            }}
                            onDragOver={(event) => {
                                event.preventDefault();
                                setDragging(true);
                            }}
                            onDragLeave={() => setDragging(false)}
                            className={cn(
                                'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-6 transition-colors',
                                dragging ? 'border-primary bg-primary/5' : 'border-border bg-muted/40',
                            )}
                        >
                            <UploadCloud className="h-5 w-5 text-muted-foreground" aria-hidden />
                            <Button type="button" size="sm" onClick={() => fileInputRef.current?.click()}>
                                {single ? 'Upload an image' : 'Upload images'}
                            </Button>
                            <p className="text-xs text-muted-foreground">
                                {single ? 'or drop one here' : 'or drop them here'} · JPEG, PNG or WebP
                            </p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                multiple={!single}
                                onChange={(e) => e.target.files && handleFileSelect(e.target.files)}
                                className="hidden"
                                data-testid="library-file-input"
                            />
                        </div>

                        {loading && images.length === 0 && (
                            <div
                                className="flex h-40 items-center justify-center gap-2 text-sm text-muted-foreground"
                                data-testid="library-loading"
                            >
                                <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden />
                                Loading your library…
                            </div>
                        )}

                        {!loading && images.length === 0 && (
                            /* The first time, the library is empty — and it used to show
                               nothing at all under the drop zone, which reads as a
                               component that failed to load rather than as a new account. */
                            <div
                                className="flex flex-col items-center gap-2 py-10 text-center"
                                data-testid="library-empty"
                            >
                                <ImageIcon className="h-6 w-6 text-muted-foreground" aria-hidden />
                                <p className="text-sm font-medium text-foreground">Nothing in your library yet</p>
                                <p className="max-w-xs text-xs text-muted-foreground">
                                    Upload your first picture above. It stays here afterwards, so you can put it
                                    on another product without uploading it twice.
                                </p>
                            </div>
                        )}

                        {images.length > 0 && (
                            <div className="mt-5 grid grid-cols-3 gap-3 sm:grid-cols-5 lg:grid-cols-6">
                                {sortedImages.map((image) => (
                                    <ImageTile
                                        key={image.id}
                                        image={image}
                                        single={single}
                                        checked={localSelectedIds.includes(image.id)}
                                        onToggle={() => toggleImageSelection(image)}
                                    />
                                ))}
                            </div>
                        )}

                        {loadingMore && (
                            <div className="flex items-center justify-center py-4" data-testid="library-loading-more">
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-3 border-t border-border bg-muted/40 px-6 py-3">
                        {/* What Done will do, said before you press it. */}
                        <p className="text-sm text-muted-foreground" data-testid="library-selection-count">
                            {uploadingCount > 0
                                ? `Uploading ${uploadingCount} ${uploadingCount === 1 ? 'image' : 'images'}…`
                                : chosen === 0
                                  ? 'Nothing selected'
                                  : `${chosen} selected`}
                        </p>
                        <div className="ml-auto flex gap-2">
                            <Button variant="outline" onClick={onClose}>
                                Cancel
                            </Button>
                            <Button onClick={handleDone} disabled={uploadingCount > 0}>
                                {single ? 'Save' : 'Done'}
                            </Button>
                        </div>
                    </div>
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
};

export default ImageSelectModal;
